// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AppIcon } from '@loa/ui-components';
import type { LOATableColumnFilter } from './types';

type LOATableColumnFilterButtonProps = Readonly<{
  columnKey: string;
  columnLabel: string;
  filter: LOATableColumnFilter;
}>;

/**
 * Caret-down button rendered at the far right of a filterable column
 * header. Opens a small popover listing the column's filter options;
 * selecting one fires {@code filter.onChange} and closes the popover.
 * Click-outside and Escape close the popover without changing state.
 *
 * <p>The popover is rendered via {@link createPortal} into
 * {@code document.body} and positioned with {@code position: fixed}
 * anchored to the trigger's {@code getBoundingClientRect}. This avoids
 * clipping by the card's {@code overflow: hidden} / the table
 * wrapper's {@code overflow-x: auto}.
 */
export function LOATableColumnFilterButton({
  columnKey,
  columnLabel,
  filter,
}: LOATableColumnFilterButtonProps) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  // Position the menu under the trigger when it opens; also reposition
  // on scroll/resize so the popover tracks the header if the user
  // scrolls the page or the table.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const place = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, left: rect.left });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const isActive = filter.value !== '' && filter.value != null;
  const ariaLabel = filter.ariaLabel ?? `Filter ${columnLabel}`;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={
          'loa-table-card__column-filter-btn'
          + (isActive ? ' loa-table-card__column-filter-btn--active' : '')
        }
        onClick={() => setOpen((prev) => !prev)}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={`column-filter-${columnKey}`}
      >
        <AppIcon name="caret down" size={14} color="#2D0A6C" />
      </button>
      {open && menuPos && createPortal(
        <ul
          ref={menuRef}
          role="listbox"
          className="loa-table-card__column-filter-menu"
          data-testid={`column-filter-menu-${columnKey}`}
          style={{ top: menuPos.top, left: menuPos.left }}
        >
          {filter.options.map((opt) => {
            const selected = filter.value === opt.value;
            return (
              <li key={opt.value || '__all__'}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={
                    'loa-table-card__column-filter-option'
                    + (selected ? ' loa-table-card__column-filter-option--selected' : '')
                  }
                  data-testid={`column-filter-option-${columnKey}-${opt.value || 'all'}`}
                  onClick={() => {
                    filter.onChange(opt.value);
                    setOpen(false);
                  }}
                >
                  {opt.label}
                </button>
              </li>
            );
          })}
        </ul>,
        document.body,
      )}
    </>
  );
}
